'use client'

import { useCallback, useEffect, useState } from 'react'

type ThreadRow = {
  id: string
  title: string
  kind: string
  status: string
  isAnchored: boolean
  replyCount: number
  lastActivityAt: string | null
}
type Message = {
  id: string
  removed: boolean
  body: string | null
  author: string | null
  createdAt: string | null
  isAuthor: boolean
}
type ThreadDetail = {
  thread: { id: string; title: string; kind: string; status: string; isAnchored: boolean }
  messages: Message[]
  canModerate: boolean
  loggedIn: boolean
}

const KIND_BADGE: Record<string, { label: string; bg: string; fg: string }> = {
  discussion: { label: 'Discussion', bg: '#eef2ff', fg: '#4338ca' },
  volunteer: { label: 'Volunteer', bg: '#ecfdf5', fg: '#0E7C61' },
  event: { label: 'Event', bg: '#fef3c7', fg: '#92400e' },
}

export default function MasjidDiscussions({ masjidId, masjidSlug, masjidName, isClaimed, memorial = false }: { masjidId: string; masjidSlug?: string; masjidName?: string; isClaimed: boolean; memorial?: boolean }) {
  /* MEMORIAL variant (the shaheed remembrance page): same threads system — same API, table,
     auth and moderation — different surface. The claim gate is skipped (a memorial has no
     committee to claim it; the API has the matching exemption), the claim CTA can never render
     (that branch is unreachable when `memorial`), and the copy reads as remembrance, not debate.
     Dark navy/gold styling via the `.memorial` class. NO seeded content — empty is empty. */
  // Deep-link the claim wizard with this masjid pre-selected (masjid/claim reads these params).
  const claimHref = `/masjid/claim?masjidSlug=${encodeURIComponent(masjidSlug || '')}&masjidId=${encodeURIComponent(masjidId || '')}&name=${encodeURIComponent(masjidName || '')}`
  const [threads, setThreads] = useState<ThreadRow[]>([])
  const [loading, setLoading] = useState(true)
  const [canModerate, setCanModerate] = useState(false)
  const [loggedIn, setLoggedIn] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  const [composeOpen, setComposeOpen] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)

  const loadList = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(`/api/masjids/${masjidId}/threads`, { cache: 'no-store' })
      const data = await res.json()
      setThreads(Array.isArray(data.threads) ? data.threads : [])
      setCanModerate(!!data.canModerate)
      setLoggedIn(!!data.loggedIn)
    } catch {
      setThreads([])
    } finally {
      setLoading(false)
    }
  }, [masjidId])

  useEffect(() => { if (isClaimed || memorial) loadList() }, [isClaimed, memorial, loadList])

  if (!isClaimed && !memorial) {
    return (
      <div className="disc-empty">
        <p>Discussions open when this masjid is claimed by its committee.</p>
        <a className="disc-btn" href={claimHref}>Claim this masjid</a>
        <style jsx>{styles}</style>
      </div>
    )
  }

  if (openId) {
    return (
      <ThreadView
        threadId={openId}
        canModerateHint={canModerate}
        memorial={memorial}
        onBack={() => { setOpenId(null); loadList() }}
        onNotice={setNotice}
      />
    )
  }

  return (
    <div className={`disc${memorial ? ' memorial' : ''}`}>
      {notice && <div className="disc-notice">{notice}</div>}
      <div className="disc-head">
        <span className="disc-count">{threads.length} {memorial ? (threads.length === 1 ? 'remembrance' : 'remembrances') : (threads.length === 1 ? 'thread' : 'threads')}</span>
        <button className="disc-btn" onClick={() => (loggedIn ? setComposeOpen(true) : (window.location.href = '/login'))}>
          {memorial ? 'Share a remembrance' : 'Start a discussion'}
        </button>
      </div>

      {loading ? (
        <div className="disc-empty"><p>Loading…</p></div>
      ) : threads.length === 0 ? (
        <div className="disc-empty">
          <p>{memorial ? 'No remembrances yet. If this masjid was part of your life or your family’s story, you are welcome to share a memory.' : 'No discussions yet — start one.'}</p>
        </div>
      ) : (
        <ul className="disc-list">
          {threads.map((t) => {
            const badge = KIND_BADGE[t.kind] || KIND_BADGE.discussion
            return (
              <li key={t.id}>
                <button className="disc-row" onClick={() => setOpenId(t.id)}>
                  <div className="disc-row-main">
                    <span className="disc-kind" style={{ background: badge.bg, color: badge.fg }}>{badge.label}</span>
                    <span className="disc-title">{t.title}</span>
                    {t.status === 'closed' && <span className="disc-closed">Closed</span>}
                  </div>
                  <div className="disc-meta">{t.replyCount} {t.replyCount === 1 ? 'reply' : 'replies'}{t.isAnchored ? ' · 📌 announcement' : ''}</div>
                </button>
              </li>
            )
          })}
        </ul>
      )}

      {composeOpen && (
        <ComposeThread
          masjidId={masjidId}
          canModerate={canModerate}
          memorial={memorial}
          onClose={() => setComposeOpen(false)}
          onDone={() => { setComposeOpen(false); setNotice(memorial ? 'Remembrance shared.' : 'Thread created.'); loadList() }}
        />
      )}
      <style jsx>{styles}</style>
    </div>
  )
}

function ThreadView({ threadId, canModerateHint, memorial = false, onBack, onNotice }: {
  threadId: string
  canModerateHint: boolean
  memorial?: boolean
  onBack: () => void
  onNotice: (m: string) => void
}) {
  const [data, setData] = useState<ThreadDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [reply, setReply] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await fetch(`/api/threads/${threadId}`, { cache: 'no-store' })
      setData(res.ok ? await res.json() : null)
    } catch { setData(null) } finally { setLoading(false) }
  }, [threadId])

  useEffect(() => { load() }, [load])

  async function sendReply() {
    const body = reply.trim()
    if (!body || busy) return
    setBusy(true); setError(null)
    try {
      const res = await fetch(`/api/threads/${threadId}/messages`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ body }),
      })
      if (res.status === 401) { window.location.href = '/login'; return }
      const j = await res.json()
      if (!res.ok) { setError(j.error || 'Could not post your reply.'); setBusy(false); return }
      setReply(''); setBusy(false); load()
    } catch { setError('Could not post your reply right now.'); setBusy(false) }
  }

  async function toggleClose(reopen: boolean) {
    if (busy) return
    setBusy(true)
    try {
      await fetch(`/api/threads/${threadId}/close`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ reopen }) })
      onNotice(reopen ? 'Thread reopened.' : 'Thread closed.'); load()
    } finally { setBusy(false) }
  }

  async function removeMessage(id: string) {
    if (busy) return
    setBusy(true)
    try { await fetch(`/api/thread-messages/${id}`, { method: 'DELETE' }); onNotice('Message removed.'); load() } finally { setBusy(false) }
  }

  async function reportMessage(id: string) {
    await fetch(`/api/thread-messages/${id}/report`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}) })
    onNotice('Reported. Thank you.'); load()
  }

  if (loading) return <div className="disc-empty"><p>Loading…</p><style jsx>{styles}</style></div>
  if (!data) return <div className="disc-empty"><p>This discussion is not available.</p><button className="disc-link" onClick={onBack}>Back</button><style jsx>{styles}</style></div>

  const { thread, messages, canModerate, loggedIn } = data
  const badge = KIND_BADGE[thread.kind] || KIND_BADGE.discussion
  const isOpen = thread.status === 'open'

  return (
    <div className={`disc${memorial ? ' memorial' : ''}`}>
      <button className="disc-link" onClick={onBack}>{memorial ? '← All remembrances' : '← All discussions'}</button>
      <div className="disc-thread-head">
        <span className="disc-kind" style={{ background: badge.bg, color: badge.fg }}>{badge.label}</span>
        <h3>{thread.title}</h3>
        {!isOpen && <span className="disc-closed">Closed</span>}
        {(canModerate || canModerateHint) && (
          <button className="disc-mod" onClick={() => toggleClose(isOpen)} disabled={busy}>{isOpen ? 'Close' : 'Reopen'}</button>
        )}
      </div>

      {messages.length === 0 ? (
        <div className="disc-empty"><p>No replies yet.</p></div>
      ) : (
        <ul className="disc-msgs">
          {messages.map((m) => (
            <li key={m.id} className={m.removed ? 'is-removed' : ''}>
              {m.removed ? (
                <p className="disc-removed-body">[removed by moderators]</p>
              ) : (
                <>
                  <div className="disc-msg-byline">{m.author}</div>
                  <p className="disc-msg-body">{m.body}</p>
                  <div className="disc-msg-actions">
                    {(m.isAuthor || canModerate) && <button onClick={() => removeMessage(m.id)}>Remove</button>}
                    {loggedIn && !m.isAuthor && <button onClick={() => reportMessage(m.id)}>Report</button>}
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {isOpen ? (
        loggedIn ? (
          <div className="disc-reply">
            <textarea value={reply} maxLength={1000} onChange={(e) => setReply(e.target.value)} placeholder="Write a reply…" rows={3} />
            {error && <div className="disc-err">{error}</div>}
            <button className="disc-btn" onClick={sendReply} disabled={busy || !reply.trim()}>{busy ? 'Posting…' : 'Reply'}</button>
          </div>
        ) : (
          <a className="disc-btn" href="/login">Sign in to reply</a>
        )
      ) : (
        <div className="disc-closed-note">This discussion is closed.</div>
      )}
      <style jsx>{styles}</style>
    </div>
  )
}

function ComposeThread({ masjidId, canModerate, memorial = false, onClose, onDone }: {
  masjidId: string
  canModerate: boolean
  memorial?: boolean
  onClose: () => void
  onDone: () => void
}) {
  const [title, setTitle] = useState('')
  const [kind, setKind] = useState('discussion')
  const [body, setBody] = useState('')
  const [announcementId, setAnnouncementId] = useState('')
  const [announcements, setAnnouncements] = useState<Array<{ id: string; title: string }>>([])
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    fetch(`/api/announcements?masjidId=${masjidId}&limit=25`)
      .then((r) => (r.ok ? r.json() : { announcements: [] }))
      .then((d) => setAnnouncements(Array.isArray(d.announcements) ? d.announcements.map((a: any) => ({ id: a.id, title: a.title })) : []))
      .catch(() => {})
  }, [masjidId])

  async function submit() {
    const t = title.trim()
    if (!t || submitting) { if (!t) setError('A thread needs a title.'); return }
    setSubmitting(true); setError(null)
    try {
      const res = await fetch(`/api/masjids/${masjidId}/threads`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: t, kind, body: body.trim() || undefined, announcementId: announcementId || undefined }),
      })
      if (res.status === 401) { window.location.href = '/login'; return }
      const j = await res.json()
      if (!res.ok) { setError(j.error || 'Could not create the thread.'); setSubmitting(false); return }
      onDone()
    } catch { setError('Could not create the thread right now.'); setSubmitting(false) }
  }

  return (
    <div className="disc-ov" onClick={onClose}>
      <div className={`disc-modal${memorial ? ' memorial-modal' : ''}`} onClick={(e) => e.stopPropagation()}>
        <h3>{memorial ? 'Share a remembrance' : 'Start a discussion'}</h3>
        <label>Title</label>
        <input value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} placeholder={memorial ? 'e.g. My grandfather prayed here' : 'What’s this about?'} />

        {/* Memorial threads are always plain remembrance discussions — no type choice, and the
            committee hint is meaningless on a masjid that has no committee. */}
        {!memorial && (
          <>
            <label>Type</label>
            <select value={kind} onChange={(e) => setKind(e.target.value)}>
              <option value="discussion">Discussion</option>
              {canModerate && <option value="volunteer">Volunteer</option>}
              {canModerate && <option value="event">Event</option>}
            </select>
            {!canModerate && <p className="disc-hint">Volunteer and event threads can be started by this masjid’s committee.</p>}
          </>
        )}

        {announcements.length > 0 && (
          <>
            <label>Anchor to an announcement (optional)</label>
            <select value={announcementId} onChange={(e) => setAnnouncementId(e.target.value)}>
              <option value="">None</option>
              {announcements.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
            </select>
            {announcementId && <p className="disc-hint">Anchored threads close automatically after 7 days.</p>}
          </>
        )}

        <label>{memorial ? 'Your memory (optional)' : 'Opening message (optional)'}</label>
        <textarea value={body} maxLength={1000} onChange={(e) => setBody(e.target.value)} rows={3} placeholder={memorial ? 'Share what you remember…' : 'Add the first message…'} />

        {error && <div className="disc-err">{error}</div>}
        <div className="disc-row-btns">
          <button className="disc-ghost" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="disc-btn" onClick={submit} disabled={submitting}>{submitting ? 'Creating…' : 'Create thread'}</button>
        </div>
      </div>
      <style jsx>{styles}</style>
    </div>
  )
}

const styles = `
  .disc { display: grid; gap: 12px; }
  .disc-head { display: flex; justify-content: space-between; align-items: center; }
  .disc-count { font-size: 13px; color: #94a3b8; font-weight: 700; }
  .disc-btn { border: 0; background: #0E7C61; color: #fff; border-radius: 10px; padding: 9px 14px; font-weight: 800; cursor: pointer; text-decoration: none; display: inline-block; }
  .disc-btn:disabled { opacity: 0.6; cursor: not-allowed; }
  .disc-ghost { border: 1px solid #cbd5e1; background: #fff; color: #475569; border-radius: 10px; padding: 9px 14px; font-weight: 800; cursor: pointer; }
  .disc-link { border: 0; background: none; color: #0E7C61; font-weight: 800; cursor: pointer; padding: 0; text-align: left; }
  .disc-notice { background: #ecfdf5; border: 1px solid #a8d9cc; color: #0E7C61; border-radius: 10px; padding: 10px 12px; font-size: 13px; font-weight: 600; }
  .disc-empty { text-align: center; color: #94a3b8; padding: 28px 12px; display: grid; gap: 12px; place-items: center; }
  .disc-empty p { margin: 0; }
  .disc-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 8px; }
  .disc-row { width: 100%; text-align: left; border: 1px solid #eaeae5; background: #fff; border-radius: 12px; padding: 12px 14px; cursor: pointer; display: grid; gap: 6px; }
  .disc-row-main { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  .disc-title { font-weight: 700; color: #1e293b; font-size: 14px; }
  .disc-kind { font-size: 11px; font-weight: 800; padding: 2px 8px; border-radius: 999px; }
  .disc-closed { font-size: 11px; font-weight: 800; color: #64748b; background: #f1f5f9; padding: 2px 8px; border-radius: 999px; }
  .disc-meta { font-size: 12px; color: #94a3b8; font-weight: 600; }
  .disc-thread-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
  .disc-thread-head h3 { margin: 0; font-size: 16px; color: #0b132b; }
  .disc-mod { margin-left: auto; border: 1px solid #cbd5e1; background: #fff; color: #475569; border-radius: 8px; padding: 5px 12px; font-weight: 800; cursor: pointer; font-size: 12px; }
  .disc-msgs { list-style: none; margin: 0; padding: 0; display: grid; gap: 10px; }
  .disc-msgs li { border: 1px solid #eaeae5; border-radius: 12px; padding: 12px 14px; }
  .disc-msgs li.is-removed { background: #f8fafc; border-style: dashed; }
  .disc-removed-body { margin: 0; color: #94a3b8; font-style: italic; font-size: 13px; }
  .disc-msg-byline { font-size: 12px; font-weight: 800; color: #64748b; }
  .disc-msg-body { margin: 4px 0 0; font-size: 14px; line-height: 1.6; color: #1e293b; white-space: pre-wrap; }
  .disc-msg-actions { margin-top: 8px; display: flex; gap: 12px; }
  .disc-msg-actions button { border: 0; background: none; color: #94a3b8; font-size: 12px; font-weight: 700; cursor: pointer; padding: 0; }
  .disc-reply { display: grid; gap: 8px; }
  .disc-reply textarea, .disc-modal textarea, .disc-modal input, .disc-modal select { width: 100%; border: 1px solid #cbd5e1; border-radius: 10px; padding: 10px 12px; font-size: 14px; font-family: inherit; box-sizing: border-box; resize: vertical; }
  .disc-reply .disc-btn { justify-self: start; }
  .disc-closed-note { color: #94a3b8; font-size: 13px; font-weight: 600; text-align: center; padding: 8px; }
  .disc-err { color: #dc2626; font-size: 13px; font-weight: 600; }
  .disc-ov { position: fixed; inset: 0; background: rgba(11,19,43,0.45); display: grid; place-items: center; padding: 16px; z-index: 60; }
  .disc-modal { background: #fff; border-radius: 16px; padding: 20px; width: 100%; max-width: 460px; max-height: 90vh; overflow-y: auto; display: grid; gap: 6px; }
  .disc-modal h3 { margin: 0 0 8px; font-size: 17px; }
  .disc-modal label { font-size: 12px; font-weight: 800; color: #475569; margin-top: 8px; }
  .disc-hint { margin: 4px 0 0; font-size: 12px; color: #94a3b8; }
  .disc-row-btns { display: flex; justify-content: flex-end; gap: 10px; margin-top: 14px; }

  /* ── MEMORIAL variant — dark remembrance theme (navy #0B132B, gold #D4AF37, serif). ── */
  .disc.memorial .disc-count { color: rgba(231,227,214,0.6); }
  .disc.memorial .disc-btn { background: #D4AF37; color: #0B132B; }
  .disc.memorial .disc-link { color: #D4AF37; }
  .disc.memorial .disc-notice { background: rgba(212,175,55,0.08); border-color: rgba(212,175,55,0.35); color: #D4AF37; }
  .disc.memorial .disc-empty { color: rgba(231,227,214,0.6); }
  .disc.memorial .disc-row { background: rgba(255,255,255,0.025); border-color: rgba(212,175,55,0.18); }
  .disc.memorial .disc-row:hover { border-color: rgba(212,175,55,0.45); }
  .disc.memorial .disc-title { color: #F4EFDF; font-family: var(--f-display, Georgia, serif); }
  .disc.memorial .disc-kind { background: rgba(212,175,55,0.14) !important; color: #D4AF37 !important; }
  .disc.memorial .disc-meta { color: rgba(231,227,214,0.55); }
  .disc.memorial .disc-closed { background: rgba(255,255,255,0.06); color: rgba(231,227,214,0.6); }
  .disc.memorial .disc-thread-head h3 { color: #F4EFDF; font-family: var(--f-display, Georgia, serif); }
  .disc.memorial .disc-mod { background: transparent; border-color: rgba(212,175,55,0.35); color: #D4AF37; }
  .disc.memorial .disc-msgs li { background: rgba(255,255,255,0.025); border-color: rgba(212,175,55,0.18); }
  .disc.memorial .disc-msgs li.is-removed { background: rgba(255,255,255,0.02); }
  .disc.memorial .disc-msg-byline { color: #D4AF37; }
  .disc.memorial .disc-msg-body { color: rgba(231,227,214,0.88); }
  .disc.memorial .disc-msg-actions button { color: rgba(231,227,214,0.5); }
  .disc.memorial .disc-reply textarea { background: rgba(255,255,255,0.04); border-color: rgba(212,175,55,0.25); color: #E7E3D6; }
  .disc.memorial .disc-closed-note { color: rgba(231,227,214,0.55); }
`
